<template>
  <div class="ledger-page">
    <b-card class="rounded-lg">
      <div class="d-flex justify-content-between align-items-start flex-wrap">
        <div class="mb-1">
          <h3 class="mb-50">
            {{ $t('ledger_links_title') }}
          </h3>
          <p class="text-muted mb-0">
            {{ $t('ledger_links_intro') }}
          </p>
        </div>
        <b-button
          v-if="canSync"
          variant="primary"
          class="rounded-pill"
          :disabled="syncing"
          @click="confirmSync"
        >
          <b-spinner
            v-if="syncing"
            small
            class="mr-50"
          />
          {{ $t('ledger_links_sync') }}
        </b-button>
      </div>
    </b-card>

    <b-card class="rounded-lg">
      <h4 class="mb-1">
        {{ $t('ledger_links_tills') }}
      </h4>

      <div
        v-if="loadingTills"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>

      <div
        v-else-if="!tills.length"
        class="text-center text-muted py-3"
      >
        {{ $t('ledger_links_empty_tills') }}
      </div>

      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="tills"
            :fields="tillFields"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(name)="data">
              <strong>{{ data.item.name }}</strong>
              <div class="text-muted small">
                {{ data.item.code }}
              </div>
            </template>
            <template #cell(type)="data">
              {{ typeLabel(data.item) }}
            </template>
            <template #cell(current_balance)="data">
              <span class="ledger-num">{{ money(data.item.current_balance) }}</span>
            </template>
            <template #cell(gl_account)="data">
              <gl-account-select
                v-if="canEditTill && chartReady"
                :value="data.item.gl_account_id"
                :accounts="accountsFor(data.item)"
                posting-only
                :disabled="!!savingIds[data.item.id]"
                :placeholder="$t('ledger_links_pick')"
                @input="id => saveTillLink(data.item, id)"
              />
              <span v-else>{{ glLabel(data.item) }}</span>
            </template>
          </b-table>
        </div>

        <div class="d-md-none">
          <div
            v-for="row in tills"
            :key="row.id"
            class="ledger-link-card"
          >
            <div class="d-flex justify-content-between">
              <strong>{{ row.name }}</strong>
              <span class="ledger-num">{{ money(row.current_balance) }}</span>
            </div>
            <div class="text-muted small mb-50">
              {{ typeLabel(row) }}
              <span v-if="row.code"> · {{ row.code }}</span>
            </div>
            <gl-account-select
              v-if="canEditTill && chartReady"
              :value="row.gl_account_id"
              :accounts="accountsFor(row)"
              posting-only
              :disabled="!!savingIds[row.id]"
              :placeholder="$t('ledger_links_pick')"
              @input="id => saveTillLink(row, id)"
            />
            <div
              v-else
              class="ledger-num"
            >
              {{ glLabel(row) }}
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <b-card class="rounded-lg">
      <h4 class="mb-1">
        {{ $t('ledger_links_wallets') }}
      </h4>

      <div
        v-if="loadingWallets"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>

      <div
        v-else-if="!wallets.length"
        class="text-center text-muted py-3"
      >
        {{ $t('ledger_links_empty_wallets') }}
      </div>

      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="wallets"
            :fields="walletFields"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(name)="data">
              {{ walletName(data.item) }}
            </template>
            <template #cell(current_balance)="data">
              <span class="ledger-num">{{ money(data.item.current_balance) }}</span>
            </template>
            <template #cell(gl_account)="data">
              {{ glLabel(data.item) }}
            </template>
          </b-table>
        </div>

        <div class="d-md-none">
          <div
            v-for="row in wallets"
            :key="`wallet-${row.id}`"
            class="ledger-link-card"
          >
            <div class="d-flex justify-content-between">
              <strong>{{ walletName(row) }}</strong>
              <span class="ledger-num">{{ money(row.current_balance) }}</span>
            </div>
            <div class="ledger-num text-muted">
              {{ glLabel(row) }}
            </div>
          </div>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BButton, BTable, BSpinner,
} from 'bootstrap-vue'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { fetchAllOperationalAccounts, updateAccount } from '@/api/accounting/accounts'
import { fetchAllWallets } from '@/api/accounting/wallets'
import { fetchAllChartAccounts } from '@/api/accounting/chartOfAccounts'
import { syncGlLinks } from '@/api/accounting/glLinks'
import {
  oneFrom, accountOptionLabel, formatLedgerMoney, enumValue, enumLabel, apiErrorMessage,
} from '@/utils/ledger'
import GlAccountSelect from './components/GlAccountSelect.vue'

const TYPE_ORDER = { cash: 0, petty_cash: 1, bank: 2 }

export default {
  name: 'GlLinks',
  components: {
    BCard, BButton, BTable, BSpinner, GlAccountSelect,
  },
  data() {
    return {
      tills: [],
      wallets: [],
      chartAccounts: [],
      chartReady: false,
      allowSave: false,
      loadingTills: false,
      loadingWallets: false,
      syncing: false,
      savingIds: {},
    }
  },
  computed: {
    canSync() {
      return this.hasPermission('edit gl_links')
    },
    canEditTill() {
      return this.hasPermission('edit accounts')
    },
    tillFields() {
      return [
        { key: 'name', label: this.$t('ledger_name') },
        { key: 'type', label: this.$t('ledger_type') },
        { key: 'current_balance', label: this.$t('ledger_links_balance') },
        { key: 'gl_account', label: this.$t('ledger_gl_account') },
      ]
    },
    walletFields() {
      return [
        { key: 'name', label: this.$t('ledger_employee') },
        { key: 'current_balance', label: this.$t('ledger_links_balance') },
        { key: 'gl_account', label: this.$t('ledger_gl_account') },
      ]
    },
  },
  mounted() {
    this.loadChart()
    this.loadTills()
    this.loadWallets()
  },
  methods: {
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    typeLabel(row) {
      return enumLabel(row.type, this.$i18n.locale)
    },
    glLabel(row) {
      if (row && row.gl_account && (row.gl_account.code || row.gl_account.name)) {
        return accountOptionLabel(row.gl_account, this.$i18n.locale)
      }
      if (row && row.gl_account_id != null && row.gl_account_id !== '') {
        return String(row.gl_account_id)
      }
      return this.$t('ledger_links_unmapped')
    },
    walletName(wallet) {
      const employee = wallet && wallet.employee
      if (employee && typeof employee === 'object') {
        return employee.name || employee.full_name || employee.name_ar || `#${wallet.id}`
      }
      if (wallet && wallet.employee_name) return wallet.employee_name
      if (wallet && wallet.employee_id) return `${this.$t('ledger_employee')} ${wallet.employee_id}`
      return wallet ? `#${wallet.id}` : ''
    },
    normalizeId(id) {
      if (id == null || id === '') return null
      const n = Number(id)
      return Number.isFinite(n) ? n : null
    },
    accountsFor(row) {
      const accounts = this.chartAccounts
      const id = row && row.gl_account_id
      if (id == null) return accounts
      if (accounts.some(account => account.id === id)) return accounts
      if (row.gl_account) {
        return accounts.concat([{
          ...row.gl_account,
          id,
          accepts_posting: true,
          is_active: true,
        }])
      }
      return accounts
    },
    chartById(id) {
      const numeric = this.normalizeId(id)
      if (numeric == null) return null
      const account = this.chartAccounts.find(row => row.id === numeric)
      if (!account) return null
      return { id: account.id, code: account.code, name: account.name }
    },
    sortTills(rows) {
      return rows.slice().sort((a, b) => {
        const ta = TYPE_ORDER[enumValue(a.type)] ?? 9
        const tb = TYPE_ORDER[enumValue(b.type)] ?? 9
        if (ta !== tb) return ta - tb
        return String(a.name || '').localeCompare(String(b.name || ''), 'ar')
      })
    },
    async loadChart() {
      try {
        this.chartAccounts = await fetchAllChartAccounts({ per_page: 100 })
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.chartReady = true
        this.armSave()
      }
    },
    armSave() {
      if (!this.chartReady || this.loadingTills) return
      this.$nextTick(() => {
        this.allowSave = true
      })
    },
    async loadTills() {
      this.allowSave = false
      this.loadingTills = true
      try {
        const rows = await fetchAllOperationalAccounts({ per_page: 100 })
        this.tills = this.sortTills(rows.map(row => ({
          ...row,
          gl_account_id: this.normalizeId(row.gl_account_id),
        })))
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loadingTills = false
        this.armSave()
      }
    },
    async loadWallets() {
      this.loadingWallets = true
      try {
        const rows = await fetchAllWallets({ status: 'active', per_page: 100 })
        this.wallets = rows.map(row => ({
          ...row,
          gl_account_id: this.normalizeId(row.gl_account_id),
        }))
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loadingWallets = false
      }
    },
    async confirmSync() {
      if (!this.canSync || this.syncing) return
      const result = await Swal.fire({
        text: this.$t('ledger_links_sync_confirm'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_links_sync'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      this.syncing = true
      try {
        const res = await syncGlLinks()
        const data = oneFrom(res) || {}
        const accounts = Number(data.accounts) || 0
        const wallets = Number(data.wallets) || 0
        this.toast(this.$t('ledger_links_sync_result', { accounts, wallets }), 'success')
        await Promise.all([this.loadTills(), this.loadWallets()])
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.syncing = false
      }
    },
    async saveTillLink(row, glAccountId) {
      if (!this.allowSave || !this.canEditTill || !row) return
      if (this.savingIds[row.id]) return
      const nextId = this.normalizeId(glAccountId)
      if (nextId === row.gl_account_id) return
      this.$set(this.savingIds, row.id, true)
      try {
        const res = await updateAccount(row.id, { gl_account_id: nextId })
        const updated = oneFrom(res) || {}
        const index = this.tills.findIndex(item => item.id === row.id)
        if (index >= 0) {
          const gl = updated.gl_account || this.chartById(updated.gl_account_id ?? nextId)
          this.$set(this.tills, index, {
            ...this.tills[index],
            gl_account_id: this.normalizeId(updated.gl_account_id ?? nextId),
            gl_account: gl,
          })
        }
        this.toast(this.$t('ledger_links_saved'), 'success')
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
        await this.loadTills()
      } finally {
        this.$set(this.savingIds, row.id, false)
      }
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'danger' ? 'AlertCircleIcon' : 'CheckIcon',
          variant,
        },
      })
    },
  },
}
</script>

<style scoped>
.ledger-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ledger-link-card {
  border-bottom: 1px solid #eee;
  padding: 0.85rem 0.15rem;
}
.ledger-link-card:last-child {
  border-bottom: 0;
}
</style>
